'use client';

import { Fragment } from 'react';
import { faArrowLeft, faXmark } from '@fortawesome/pro-light-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartGuidanceBlock } from './cart-guidance-block.component';
import { TELEHEALTH_IMPORTANT_INFO, buildGuidanceBlocks, hasTelehealthItem } from './_utils/build-guidance-blocks.util';

import { Button } from '@/components/ui/button/button.component';
import { Checkbox } from '@/components/ui/checkbox/checkbox.component';
import { Icon } from '@/components/ui/icon.component';
import { SheetClose } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useCart } from '@/hooks/use-cart.hook';
import { useCheckoutNavigation } from '@/hooks/navigation/use-checkout-navigation.hook';
import {
  acceptGuidance,
  buildCartSignature,
  guidanceAcceptanceStore,
  revokeGuidance,
} from '@/store/cart/guidance-acceptance.store';
import { trackGuidanceAccepted } from '@/utils/cart/cart-analytics.util';
import { cartStore } from '@/store/cart/cart.store';
import { authStore } from '@/store/auth.store';

export const GUIDANCE_INTRO = 'É preciso concordar com as informações importantes sobre cada procedimento:';

interface CartGuidanceProps {
  onBack: () => void;
}

function GuidanceDivider() {
  return <hr className="border-t border-gray-300" />;
}

export function CartGuidance({ onBack }: CartGuidanceProps) {
  const { items } = useCart();
  const { openCheckout, isOpening } = useCheckoutNavigation();
  const acceptedSignature = useStore(guidanceAcceptanceStore, (state) => state.acceptedSignature);
  const cartId = useStore(cartStore, (state) => state.cart?.id);
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);

  const signature = buildCartSignature(items);
  const isAccepted = acceptedSignature !== null && acceptedSignature === signature;
  const blocks = buildGuidanceBlocks(items);
  const showsTelehealthInfo = hasTelehealthItem(items);

  const handleAcceptChange = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      acceptGuidance(signature);
      trackGuidanceAccepted(items, cartId, authenticatedUser?.id);

      return;
    }

    revokeGuidance();
  };

  return (
    <div className="relative flex h-full flex-col" data-testid={DataTestIdEnum.CART_GUIDANCE}>
      <header className="flex shrink-0 items-center justify-between gap-2 px-6 pb-4 pt-5">
        <h2
          className="text-2xl font-bold leading-8 text-gray-900"
          data-testid={DataTestIdEnum.CART_GUIDANCE_TITLE}
        >
          Orientações
        </h2>

        <SheetClose
          aria-label="Fechar carrinho"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-gray-500 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          data-testid={DataTestIdEnum.CART_GUIDANCE_CLOSE_BUTTON}
        >
          <Icon icon={faXmark} className="h-5 w-5" />
        </SheetClose>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-44">
        <div className="flex flex-col gap-4 rounded-lg border border-gray-300 p-4">
          <Typography variant={TypographyVariant.BODY_TEXT} data-testid={DataTestIdEnum.CART_GUIDANCE_INTRO}>
            {GUIDANCE_INTRO}
          </Typography>

          <GuidanceDivider />

          {showsTelehealthInfo && (
            <>
              <section
                className="flex flex-col items-start gap-2"
                data-testid={DataTestIdEnum.CART_GUIDANCE_IMPORTANT_INFO}
              >
                <Icon icon={faCircleExclamation} className="text-2xl text-warning-500" aria-hidden />
                <Typography variant={TypographyVariant.BODY_TEXT}>{TELEHEALTH_IMPORTANT_INFO}</Typography>
              </section>

              <GuidanceDivider />
            </>
          )}

          {blocks.map((block, index) => (
            <Fragment key={block.id}>
              {index > 0 && <GuidanceDivider />}
              <CartGuidanceBlock block={block} />
            </Fragment>
          ))}
        </div>
      </div>

      <footer className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-b from-white/0 via-white via-20% to-white px-3 pb-3 pt-8">
        <div className="flex flex-col gap-4 rounded-xxl border border-gray-300 bg-gray-100 p-2">
          <Checkbox
            container="text"
            align="center"
            htmlFor="cart-guidance-accept"
            checked={isAccepted}
            data-testid={DataTestIdEnum.CART_GUIDANCE_ACCEPT_CHECKBOX}
            onCheckedChange={handleAcceptChange}
          >
            Li e estou ciente das instruções.
          </Checkbox>

          <div className="flex items-center gap-3">
            <Button
              variant="text"
              color="primary"
              className="flex-1"
              startIcon={faArrowLeft}
              startIconSize="sm"
              data-testid={DataTestIdEnum.CART_GUIDANCE_BACK_BUTTON}
              onClick={onBack}
            >
              Voltar
            </Button>
            <Button
              className="flex-1"
              disabled={!isAccepted}
              isLoading={isOpening}
              data-testid={DataTestIdEnum.CART_GUIDANCE_CONTINUE_BUTTON}
              onClick={() => {
                void openCheckout();
              }}
            >
              Prosseguir
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
